import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  InputAdornment,
  Divider,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { Package } from '../../../types/Venue';

interface PackageAvailabilityFormProps {
  packageData: Partial<Package>;
  onChange: (data: Partial<Package>) => void;
}

interface TimeSlot {
  start: string;
  end: string;
}

const weekdays = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const PackageAvailabilityForm: React.FC<PackageAvailabilityFormProps> = ({
  packageData,
  onChange
}) => {
  const [newTimeSlot, setNewTimeSlot] = useState<TimeSlot>({ start: '18:00', end: '20:00' });
  const [error, setError] = useState<string | null>(null);

  const handleDayChange = (dayValue: number) => {
    const currentDays = packageData.availability?.daysAvailable || [];
    let updatedDays;
    
    if (currentDays.includes(dayValue)) {
      updatedDays = currentDays.filter(day => day !== dayValue);
    } else {
      updatedDays = [...currentDays, dayValue];
    }
    
    // Sort days to display in order (Sunday - Saturday)
    updatedDays.sort((a, b) => a - b);
    
    const updatedAvailability = {
      ...packageData.availability,
      daysAvailable: updatedDays,
      timeSlots: packageData.availability?.timeSlots || []
    };
    
    onChange({ availability: updatedAvailability });
  };

  const handleDateChange = (date: Date | null, type: 'start' | 'end') => {
    const updatedAvailability = {
      ...packageData.availability,
      [type === 'start' ? 'startDate' : 'endDate']: date,
      daysAvailable: packageData.availability?.daysAvailable || [],
      timeSlots: packageData.availability?.timeSlots || []
    };
    
    onChange({ availability: updatedAvailability });
  };

  const handleAddTimeSlot = () => {
    if (!newTimeSlot.start || !newTimeSlot.end) {
      setError('Both start and end times are required');
      return;
    }
    
    if (newTimeSlot.start >= newTimeSlot.end) {
      setError('End time must be after start time');
      return;
    }
    
    const currentTimeSlots = packageData.availability?.timeSlots || [];
    
    // Check for overlapping time slots
    const isOverlapping = currentTimeSlots.some(slot => {
      return (
        (newTimeSlot.start >= slot.start && newTimeSlot.start < slot.end) ||
        (newTimeSlot.end > slot.start && newTimeSlot.end <= slot.end) ||
        (newTimeSlot.start <= slot.start && newTimeSlot.end >= slot.end)
      );
    });
    
    if (isOverlapping) {
      setError('Time slot overlaps with an existing time slot');
      return;
    }
    
    const updatedTimeSlots = [...currentTimeSlots, newTimeSlot];
    
    // Sort time slots by start time
    updatedTimeSlots.sort((a, b) => a.start.localeCompare(b.start));
    
    const updatedAvailability = {
      ...packageData.availability,
      timeSlots: updatedTimeSlots,
      daysAvailable: packageData.availability?.daysAvailable || []
    };
    
    onChange({ availability: updatedAvailability });
    setNewTimeSlot({ start: '18:00', end: '20:00' });
    setError(null);
  };

  const handleRemoveTimeSlot = (index: number) => {
    const currentTimeSlots = packageData.availability?.timeSlots || [];
    const updatedTimeSlots = [...currentTimeSlots];
    updatedTimeSlots.splice(index, 1);
    
    const updatedAvailability = {
      ...packageData.availability,
      timeSlots: updatedTimeSlots,
      daysAvailable: packageData.availability?.daysAvailable || []
    };
    
    onChange({ availability: updatedAvailability });
  };

  const formatTimeDisplay = (time: string) => {
    try {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch (e) {
      return time;
    }
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom>
        Package Availability
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Define when this package is available for booking.
      </Typography>
      
      <Grid container spacing={3}>
        {/* Date Range Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Date Range (Optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Leave blank if this package is available indefinitely.
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                type="date"
                label="Start Date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={packageData.availability?.startDate 
                  ? new Date(packageData.availability.startDate).toISOString().split('T')[0] 
                  : ''}
                onChange={(e) => {
                  const date = e.target.value ? new Date(e.target.value) : null;
                  handleDateChange(date, 'start');
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="date"
                label="End Date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={packageData.availability?.endDate 
                  ? new Date(packageData.availability.endDate).toISOString().split('T')[0] 
                  : ''}
                onChange={(e) => {
                  const date = e.target.value ? new Date(e.target.value) : null;
                  handleDateChange(date, 'end');
                }}
                inputProps={{
                  min: packageData.availability?.startDate 
                    ? new Date(packageData.availability.startDate).toISOString().split('T')[0]
                    : undefined
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        
        {/* Days of Week Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Days of Week
          </Typography>
          <Paper sx={{ p: 2 }}>
            <FormGroup row>
              {weekdays.map((day) => (
                <FormControlLabel
                  key={day.value}
                  control={
                    <Checkbox
                      checked={(packageData.availability?.daysAvailable || []).includes(day.value)}
                      onChange={() => handleDayChange(day.value)}
                    />
                  }
                  label={day.label}
                  sx={{ width: { xs: '50%', sm: '33%', md: '25%' } }}
                />
              ))}
            </FormGroup>
          </Paper>
        </Grid>
        
        {/* Time Slots Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Time Slots
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                value={newTimeSlot.start}
                onChange={(e) => setNewTimeSlot({ ...newTimeSlot, start: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TimeIcon />
                    </InputAdornment>
                  )
                }}
                inputProps={{
                  step: 900 // 15 minutes
                }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                value={newTimeSlot.end}
                onChange={(e) => setNewTimeSlot({ ...newTimeSlot, end: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TimeIcon />
                    </InputAdornment>
                  )
                }}
                inputProps={{
                  step: 900 // 15 minutes
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ height: '100%' }}
                startIcon={<AddIcon />}
                onClick={handleAddTimeSlot}
              >
                Add
              </Button>
            </Grid>
          </Grid>
          
          <Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
            {(packageData.availability?.timeSlots || []).length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No time slots added yet. Add at least one time slot when this package is available.
                </Typography>
              </Box>
            ) : (
              <List>
                {(packageData.availability?.timeSlots || []).map((slot, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <Divider component="li" />}
                    <ListItem>
                      <ListItemText
                        primary={`${formatTimeDisplay(slot.start)} - ${formatTimeDisplay(slot.end)}`}
                        secondary={`${slot.start} - ${slot.end}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          onClick={() => handleRemoveTimeSlot(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2">Quick Add Time Slots:</Typography>
        <Grid container spacing={1} sx={{ mt: 1 }}>
          <Grid item>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => {
                setNewTimeSlot({ start: '11:30', end: '13:30' });
              }}
            >
              Lunch (11:30 - 1:30)
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => {
                setNewTimeSlot({ start: '18:00', end: '20:00' });
              }}
            >
              Dinner (6:00 - 8:00)
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => {
                setNewTimeSlot({ start: '19:00', end: '21:00' });
              }}
            >
              Evening (7:00 - 9:00)
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => {
                setNewTimeSlot({ start: '20:00', end: '22:00' });
              }}
            >
              Late Dinner (8:00 - 10:00)
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default PackageAvailabilityForm;
